import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  UserCircleIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline'
import AuthForm from './AuthForm'
import logo from '../logo.svg'

function AuthHeader({ showLogo = true, onLogoClick }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [showAuthForm, setShowAuthForm] = useState(false)
  const [showUserDropdown, setShowUserDropdown] = useState(false)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserDropdown])

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick()
    } else {
      navigate('/')
    }
  }

  return (
    <>
      {/* Auth Form Overlay */}
      {showAuthForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="relative w-full max-w-md mx-4 bg-white rounded-lg shadow-lg p-6">
            <button
              onClick={() => setShowAuthForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <AuthForm onClose={() => setShowAuthForm(false)} />
          </div>
        </div>
      )}

      {/* Header */}
      <header className="shadow-sm border-b border-gray-200" style={{ backgroundColor: '#D9E4F8' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center">
              {showLogo && (
                <button 
                  onClick={handleLogoClick}
                  className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <img 
                      src={logo} 
                      alt="Points.FYI Logo" 
                      className="w-6 h-6 text-gray-500"
                    />
                  </div>
                  <h1 className="text-xl font-semibold" style={{ color: '#5F6A7C' }}>Points.FYI</h1>
                </button>
              )}
            </div>
            <div className="flex items-center space-x-3">
              {user ? (
                <div className="relative user-dropdown">
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={() => setShowUserDropdown(!showUserDropdown)}
                      className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 focus:outline-none"
                    >
                      <div className="flex items-center space-x-2">
                        <UserCircleIcon className="w-6 h-6 text-gray-400" />
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{user.username}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                                              <svg className={'w-5 h-5 transition-transform duration-200 ' + (showUserDropdown ? 'rotate-180' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* User Dropdown Menu */}
                  {showUserDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <div className="flex items-center space-x-3">
                          <UserCircleIcon className="w-6 h-6 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.username}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </div>

                      <button 
                        onClick={() => navigate('/dashboard')}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <UserCircleIcon className="w-4 h-4 mr-2 text-gray-500" />
                        Dashboard
                      </button>
                      <button 
                        onClick={() => logout()}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2 text-red-500" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  onClick={() => setShowAuthForm(true)}
                  className="hover:text-gray-900 text-base font-medium"
                  style={{ color: '#5F6A7C' }}
                >
                  SIGN UP/LOG IN
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  )
}

export default AuthHeader
